import React, { useEffect, useMemo, useRef } from "react"

import * as Sentry from "@sentry/nextjs"
import {
    HydrationBoundary,
    QueryClient,
    QueryClientProvider,
} from "@tanstack/react-query"
import ErrorFallback from "components/atoms/ErrorFallback"
import HelpButton from "components/atoms/HelpButton"
import ErrorBoundary from "components/page_components/ErrorBoundary"
import { TransferProvider } from "context/TransferContext"
import { UserProvider } from "context/UserContext"
import { CartProvider } from "hooks/useCartV2"
import { useLang } from "hooks/useLang"
import { IBM_Plex_Sans_Arabic } from "next/font/google"
import { IBM_Plex_Sans } from "next/font/google"
import Router, { useRouter } from "next/router"
import { DefaultSeo } from "next-seo"
import { ArSEO, EnSEO } from "seo"
import { LogoSchema, WebsiteSchema, OrganizationSchema } from "seo/schema"
import Script from "next/script"
import { NextWebVitalsMetric } from "next/app"
import SmartBanner from "components/SmartBanner/SmartBanner"
import axios from "axios"
import FeatureFlagProvider from "providers/FeatureFlagProvider"
import { ThemeProvider } from "next-themes"
import UnifiedAnalyticsProvider from "components/Analytics/UnifiedAnalyticsProvider"
import { useMaintenanceMode } from "hooks/useMaintenanceMode"
import MaintenanceScreen from "components/page_components/MaintenanceScreen"
import ProgressBar from "@badrap/bar-of-progress"
import "./../styles/global.scss"
import "./../styles/index.scss"
import { ModalProvider } from "context/modalContext"

Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    release: `${process.env.NEXT_PUBLIC_APP_VERSION}-${process.env.NEXT_PUBLIC_FEATURE_ENVIRONMENT}`,
    integrations: [],
    tracesSampleRate: 0.2,
    replaysOnErrorSampleRate: 1.0,
    environment: process.env.NEXT_PUBLIC_FEATURE_ENVIRONMENT || "local",

    // Filter out errors from browser extensions and third-party scripts
    beforeSend(event, hint) {
        const error = hint.originalException
        const message =
            event.message ||
            (error instanceof Error ? error.message : "")

        // Ignore SecurityError from browser extensions
        if (error instanceof Error && error.name === "SecurityError") {
            // Common browser extension security errors
            if (
                error.message.includes("removeEventListener") ||
                error.message.includes("addEventListener") ||
                error.message.includes("security policy")
            ) {
                return null
            }
        }

        // Ignore errors from browser extensions based on stack trace
        if (event.exception?.values) {
            for (const exception of event.exception.values) {
                if (exception.stacktrace?.frames) {
                    for (const frame of exception.stacktrace.frames) {
                        const filename = frame.filename || ""

                        // Filter browser extension errors
                        if (
                            filename.includes("chrome-extension://") ||
                            filename.includes("moz-extension://") ||
                            filename.includes("safari-extension://") ||
                            filename.includes("webkit-masked-url://")
                        ) {
                            return null
                        }

                        // Filter common third-party script patterns
                        if (
                            filename.includes("googletagmanager.com") ||
                            filename.includes("google-analytics.com") ||
                            filename.includes("facebook.net") ||
                            filename.includes("doubleclick.net") ||
                            filename.includes("clarity.ms")
                        ) {
                            return null
                        }
                    }
                }
            }
        }

        // Ignore non-Error exceptions (often from extensions)
        if (error && typeof error === "object" && !(error as any).stack) {
            return null
        }

        // Ignore ResizeObserver errors (browser quirks, not actionable)
        if (
            error instanceof Error &&
            error.message.includes("ResizeObserver loop")
        ) {
            return null
        }

        if (
            message.includes("AbortError") ||
            message.includes("Request aborted")
        ) {
            return null
        }

        return event
    },

    // Ignore specific error messages
    ignoreErrors: [
        // Browser extension errors
        "SecurityError",
        "Non-Error promise rejection captured",
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",

        // Network errors that are not actionable
        "Network request failed",
        "NetworkError",
        "Failed to fetch",
        "AbortError",
        "Request aborted",

        // Browser quirks
        "cancelled",
        "timeout",

        // Third-party script errors
        /gtag/i,
        /google-analytics/i,
        /facebook/i,
    ],

    // Only capture errors from our domain
    denyUrls: [
        // Browser extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,

        // Third-party scripts
        /googletagmanager\.com/i,
        /google-analytics\.com/i,
        /facebook\.net/i,
        /doubleclick\.net/i,
        /clarity\.ms/i,
    ],
})

const ibmPlexSansArabic = IBM_Plex_Sans_Arabic({
    weight: ["200", "400", "600"],
    display: "swap",
    style: "normal",
    subsets: ["latin"],
})

const ibmPlexSans = IBM_Plex_Sans({
    weight: ["200", "400", "600"],
    display: "swap",
    style: "normal",
    subsets: ["latin"],
})

export function reportWebVitals(metric: NextWebVitalsMetric) {
    // Use our analytics service to report Web Vitals
    if (typeof window !== "undefined" && window.analytics) {
        try {
            window.analytics.track({
                name: metric.name,
                properties: {
                    category:
                        metric.label === "web-vital"
                            ? "Web Vitals"
                            : "Next.js custom metric",
                    value: Math.round(
                        metric.name === "CLS"
                            ? metric.value * 1000
                            : metric.value
                    ),
                    label: metric.id,
                    nonInteraction: true,
                },
            })
        } catch (error) {
            console.error("Error tracking web vitals:", error)
        }
    }
}

export default function App({ Component, pageProps }) {
    const { isAr } = useLang()
    const isInMaintenance = useMaintenanceMode(pageProps.isInMaintenance)

    const router = useRouter()

    useEffect(() => {
        const isSafari = /^((?!chrome|android).)*safari/i.test(
            navigator.userAgent
        )
        if (isSafari) {
            document.body.classList.add("safari")
        }

        return () => {
            if (isSafari) {
                document.body.classList.remove("safari")
            }
        }
    }, [])

    const [queryClient] = React.useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        staleTime: 5 * 60 * 1000, // 5 minutes
                        gcTime: 10 * 60 * 1000, // 10 minutes
                        refetchOnWindowFocus: false, // Reduce unnecessary refetches
                        retry: 1,
                    },
                },
            })
    )

    const resetError = () => {
        router.push("/")
    }
    const handleError = (error: Error) => {
        Sentry.captureException(error)
    }

    const { locale } = useRouter()
    const dir = locale === "ar-SA" ? "rtl" : "ltr"

    useEffect(() => {
        document.documentElement.dir = dir
    }, [dir])

    // Fix: Move ProgressBar to useEffect with proper cleanup to prevent memory leak
    const progressRef = useRef<ProgressBar | null>(null)
    useEffect(() => {
        progressRef.current = new ProgressBar({
            size: "2px",
            color: "#FD0058",
            className: "z-[199]",
            delay: 80,
        })

        const handleStart = () => progressRef.current?.start()
        const handleFinish = () => progressRef.current?.finish()

        Router.events.on("routeChangeStart", handleStart)
        Router.events.on("routeChangeComplete", handleFinish)
        Router.events.on("routeChangeError", handleFinish)

        return () => {
            Router.events.off("routeChangeStart", handleStart)
            Router.events.off("routeChangeComplete", handleFinish)
            Router.events.off("routeChangeError", handleFinish)
        }
    }, [])

    // Memoize SEO config to prevent recalculation on every render
    // Exclude default OG images on event pages to prevent them from overriding event-specific images
    const isEventPage = router.pathname.startsWith("/events/")
    const seo = useMemo(() => {
        const baseSeo = isAr ? ArSEO : EnSEO
        if (isEventPage) {
            return {
                ...baseSeo,
                openGraph: {
                    ...baseSeo.openGraph,
                    images: undefined,
                },
            }
        }
        return baseSeo
    }, [isAr, isEventPage])

    // Fix: Move axios headers setup to useEffect to avoid running on every render
    useEffect(() => {
        axios.defaults.headers.common["x-from-client"] = "frontend"
        axios.defaults.headers.common["x-client-version"] =
            process.env.NEXT_PUBLIC_APP_VERSION
        axios.defaults.headers.common["x-client-environment"] =
            process.env.NODE_ENV
    }, [])

    // Update language header when locale changes
    useEffect(() => {
        axios.defaults.headers.common["accept-language"] = isAr ? "ar" : "en"
    }, [isAr])

    return (
        <>
            <FeatureFlagProvider>
                <UnifiedAnalyticsProvider>
                    <QueryClientProvider client={queryClient}>
                        <HydrationBoundary state={pageProps.dehydratedState}>
                            <ModalProvider>
                                <UserProvider>
                                    <CartProvider>
                                        <ErrorBoundary
                                            FallbackComponent={ErrorFallback}
                                            onReset={resetError}
                                            onError={handleError}
                                        >
                                            <ThemeProvider
                                                defaultTheme="dark"
                                                attribute="class"
                                                forcedTheme={
                                                    Component.theme || null
                                                }
                                            >
                                                {false ? (
                                                    <MaintenanceScreen />
                                                ) : (
                                                    <>
                                                        <DefaultSeo {...seo} />
                                                        {/* Schema.org structured data */}
                                                        <LogoSchema />
                                                        <WebsiteSchema />
                                                        <OrganizationSchema />
                                                        {/* <SmartBanner /> */}
                                                        <TransferProvider>
                                                            <div
                                                                lang={locale}
                                                                dir={dir}
                                                                className={
                                                                    isAr
                                                                        ? ibmPlexSansArabic.className
                                                                        : ibmPlexSans.className
                                                                }
                                                            >
                                                                <Script
                                                                    id="ua-support"
                                                                    strategy="afterInteractive"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: `
													var ua = window.navigator.userAgent;
													var isIE = /MSIE|Trident/.test(ua);
													
													if ( isIE ) {
														window.location = window.location.protocol + '//' + window.location.host + '/notsupported.html';
													}`,
                                                                    }}
                                                                />
                                                                {/* <ReactQueryDevtools
                                                                    initialIsOpen={
                                                                        false
                                                                    }
                                                                /> */}

                                                                <Component
                                                                    {...pageProps}
                                                                />
                                                                <div className="sticky bottom-10 left-0 z-50 hidden md:block">
                                                                    <HelpButton />
                                                                </div>
                                                            </div>
                                                        </TransferProvider>
                                                    </>
                                                )}
                                            </ThemeProvider>
                                        </ErrorBoundary>
                                    </CartProvider>
                                </UserProvider>
                            </ModalProvider>
                        </HydrationBoundary>
                    </QueryClientProvider>
                </UnifiedAnalyticsProvider>
            </FeatureFlagProvider>
        </>
    )
}
